import Constants from 'expo-constants';

import {
  CartPaymentCardType,
  PaymentProcessor,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import logger from '@fhs-legacy/frontend/src/utils/logger';

import { PipelineBaseStep } from '../core/pipeline-base-step';
import type {
  GooglePayContext,
  IGoogleConfig,
  PipelineStepContext,
  PipelineStepResult,
} from '../payment-types';

interface PaymentConfig {
  region: string;
  currencyCode: string;
  totalAmount: string;
  gateway: string;
  gatewayMerchantId: string;
  merchantName: string;
  merchantId?: string;
  environment: string;
  networks: IGoogleConfig['paymentsNetworks'];
}

interface BillingAddress {
  locality: string;
  postalCode: string;
  region: string;
  streetAddress: string;
}

/**
 * Web Version Google Pay Step
 * 1. Validates configuration and pipeline context
 * 2. Builds and executes payment requests (Worldpay or Orbital)
 * 3. Parses and transforms payment responses
 * 4. Returns payment details for order commit
 *
 * Uses Google Payments JavaScript API (google.payments.api.PaymentsClient)
 * instead of the native SDK which only works on Android
 *
 * When user cancels the google pay screen it is treated as a warning.
 * vantiv is the legacy name of WorldPay
 */
export class GooglePayStep extends PipelineBaseStep {
  readonly name = 'GooglePayStep';
  private region: 'US' | 'CA';

  constructor() {
    super();
    this.region = Constants?.expoConfig?.extra?.eas?.buildRegion;
  }

  async execute(pipelineContext: PipelineStepContext): Promise<PipelineStepResult> {
    if (pipelineContext.paymentMethod !== 'googlePay') {
      return this.success();
    }

    const googleConfig = this.getConfiguration(pipelineContext);
    if (!googleConfig) {
      return this.error(
        'GOOGLE_PAY_CONFIG_MISSING',
        'Google Pay configuration not found',
        'Google Pay is not available at this time. Please use another payment method. Error Code: (GConfig Missing)',
        'GooglePayStep'
      );
    }

    // Step 2: Make sure the google api is still available
    const apiValidationResult = this.validateGooglePayApi();
    if (apiValidationResult) {
      return apiValidationResult;
    }

    // Step 3: Build and check the payment config per payment processor
    try {
      const paymentConfig = this.buildPaymentConfig(pipelineContext, googleConfig);

      const validationResult = this.validatePaymentConfig(paymentConfig, pipelineContext.processor);
      if (validationResult) {
        return validationResult;
      }

      const paymentData = await this.executePaymentRequest(pipelineContext, paymentConfig);

      // Step 4: Execute the request and assemble pipeline context for commit order
      return this.handlePaymentResponse(paymentData, pipelineContext);
    } catch (error) {
      const statusCode = (error as { statusCode?: string })?.statusCode;
      const message = error instanceof Error ? error.message : String(error);
      if (statusCode === 'CANCELED' || /cancel/i.test(message)) {
        return this.handlePaymentCancellation();
      }

      logger.error({
        message: '[GooglePay] Payment request failed',
        error: message,
        stack: error instanceof Error ? error.stack : undefined,
      });
      return this.error(
        'GOOGLE_PAY_ERROR',
        message || 'Unknown error',
        'Google Pay payment did not complete. Please try again. Error Code: (Google Pay Failed)',
        'GooglePayStep'
      );
    }
  }

  private getConfiguration(context?: PipelineStepContext): IGoogleConfig | null {
    const fromConstants =
      Constants?.expoConfig?.extra?.eas?.US?.google ||
      Constants?.expoConfig?.extra?.eas?.CA?.google ||
      null;
    return context?.googleConfig ?? fromConstants;
  }

  private validateGooglePayApi(): PipelineStepResult | null {
    if (typeof google === 'undefined' || !google.payments?.api?.PaymentsClient) {
      logger.error({
        message: '[GooglePay] Google Pay API not loaded on web',
      });
      return this.error(
        'GOOGLE_PAY_API_NOT_LOADED',
        'Google Pay API not available',
        'Google Pay is not available. Please use another payment method. Error Code: (GApi Not Loaded)'
      );
    }

    return null;
  }

  private buildPaymentConfig(
    pipelineContext: GooglePayContext,
    googleConfig: IGoogleConfig
  ): PaymentConfig {
    const currencyCode = this.region === 'CA' ? 'CAD' : 'USD';
    const totalAmount = (pipelineContext.cartTotal / 100).toString();
    const gatewayMerchantId =
      this.region === 'CA'
        ? googleConfig.paymentsGatewayMerchantId.ca
        : googleConfig.paymentsGatewayMerchantId.us;

    const networks = (googleConfig.paymentsNetworks ?? []).filter(
      (n: unknown) => typeof n === 'string' && n.length > 0
    );
    return {
      region: this.region,
      currencyCode,
      totalAmount,
      gateway: googleConfig.paymentsGateway,
      gatewayMerchantId,
      merchantName: googleConfig.googleMerchantName,
      merchantId: googleConfig.googleMerchantId,
      environment: googleConfig.paymentsEnvironment,
      networks,
    };
  }

  private validatePaymentConfig(
    config: PaymentConfig,
    processor: PaymentProcessor
  ): PipelineStepResult | null {
    if (!config.gateway) {
      logger.error({
        message: '[GooglePay] Missing paymentsGateway in config',
        processor,
      });
      return this.error(
        'GOOGLE_PAY_CONFIG_MISSING',
        'Gateway not configured',
        'Google Pay is not configured properly. Please use another payment method. Error Code: (GConfig Gateway Missing)'
      );
    }

    if (!config.gatewayMerchantId) {
      logger.error({
        message: '[GooglePay] Missing gatewayMerchantId in config',
        processor,
        region: config.region,
      });
      return this.error(
        'GOOGLE_PAY_CONFIG_MISSING',
        'Gateway merchant ID not configured',
        'Google Pay is not configured properly. Please use another payment method. Error Code: (GConfig GatewayMerchantId Missing)'
      );
    }

    return null;
  }

  private async executePaymentRequest(
    pipelineContext: PipelineStepContext,
    config: PaymentConfig
  ): Promise<google.payments.api.PaymentData> {
    this.logPaymentRequest(pipelineContext.processor, config);

    const tokenizationSpecification = this.buildTokenizationSpecification(pipelineContext, config);

    const paymentDataRequest = this.buildPaymentDataRequest(config, tokenizationSpecification);

    const paymentsClient = this.createPaymentsClient(config);

    logger.info({
      message: '[GooglePay] Opening Google Pay sheet (Web)',
    });

    const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);

    // this.logPaymentResponse(paymentData);

    return paymentData;
  }

  private buildTokenizationSpecification(
    pipelineContext: PipelineStepContext,
    config: PaymentConfig
  ): google.payments.api.PaymentMethodTokenizationSpecification {
    if (pipelineContext.processor === PaymentProcessor.WORLDPAY) {
      return this.buildWorldpayTokenization(pipelineContext, config);
    } else {
      return this.buildOrbitalTokenization(config);
    }
  }

  private buildWorldpayTokenization(
    pipelineContext: PipelineStepContext,
    config: PaymentConfig
  ): google.payments.api.PaymentMethodTokenizationSpecification {
    const merchantOrderId = pipelineContext.rbiOrderId.slice(0, 20);
    const merchantTransactionId = merchantOrderId;
    const env = Constants?.expoConfig?.extra?.eas?.env;
    const merchantReportGroup = `${env}-FHS-${config.region}`.toUpperCase();

    logger.info({
      message: '[GooglePay] Using Worldpay tokenization',
      merchantOrderId,
      merchantTransactionId,
      merchantReportGroup,
      env,
    });

    return {
      type: 'PAYMENT_GATEWAY',
      parameters: {
        gateway: config.gateway,
        'vantiv:merchantPayPageId': config.gatewayMerchantId,
        'vantiv:merchantOrderId': merchantOrderId,
        'vantiv:merchantTransactionId': merchantTransactionId,
        'vantiv:merchantReportGroup': merchantReportGroup,
      },
    };
  }

  private buildOrbitalTokenization(
    config: PaymentConfig
  ): google.payments.api.PaymentMethodTokenizationSpecification {
    logger.info({
      message: '[GooglePay] Using Orbital tokenization',
    });

    return {
      type: 'PAYMENT_GATEWAY',
      parameters: {
        gateway: config.gateway,
        gatewayMerchantId: config.gatewayMerchantId,
      },
    };
  }

  private buildPaymentDataRequest(
    config: PaymentConfig,
    tokenizationSpecification: google.payments.api.PaymentMethodTokenizationSpecification
  ): google.payments.api.PaymentDataRequest {
    const countryCode = config.region === 'CA' ? 'CA' : 'US';

    const cardPaymentMethod: google.payments.api.PaymentMethodSpecification = {
      type: 'CARD',
      parameters: {
        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
        allowedCardNetworks: config.networks as google.payments.api.CardNetwork[],
        assuranceDetailsRequired: true,
        billingAddressRequired: false,
        billingAddressParameters: {
          format: 'FULL',
          phoneNumberRequired: false,
        },
      },
      tokenizationSpecification,
    };

    return {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [cardPaymentMethod],
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPrice: config.totalAmount,
        currencyCode: config.currencyCode,
        countryCode,
      },
      merchantInfo: {
        merchantName: config.merchantName || 'Restaurant',
        merchantId: config.merchantId,
      },
    };
  }

  private createPaymentsClient(config: PaymentConfig): google.payments.api.PaymentsClient {
    const env = config.environment === 'test' ? 'TEST' : 'PRODUCTION';
    const clientConfig: google.payments.api.PaymentOptions = {
      environment: env,
    };

    if (config.merchantId && config.merchantName) {
      clientConfig.merchantInfo = {
        merchantName: config.merchantName,
        merchantId: config.merchantId,
      };
    }

    return new google.payments.api.PaymentsClient(clientConfig);
  }

  private handlePaymentResponse(
    paymentData: google.payments.api.PaymentData,
    context: PipelineStepContext
  ): PipelineStepResult {
    if (!paymentData || !paymentData.paymentMethodData) {
      return this.handlePaymentCancellation();
    }

    const googlePayDetails = this.parsePaymentResponse(paymentData, context.processor);
    const creditType = CartPaymentCardType.GOOGLE_PAY;

    logger.info({
      message: '[GooglePay] Payment successful',
      processor: context.processor,
      paymentType: googlePayDetails.PaymentMethodData.paymentType,
      creditType,
    });

    return this.success({
      googlePayDetails,
      creditType,
    } as Partial<GooglePayContext>);
  }

  private handlePaymentCancellation(): PipelineStepResult {
    logger.warn({
      message: '[GooglePay] No payment data returned (user cancelled)',
    });
    return this.error('GOOGLE_PAY_CANCELLED', 'User cancelled Google Pay');
  }

  private parsePaymentResponse(
    paymentData: google.payments.api.PaymentData,
    processor: PaymentProcessor
  ) {
    const { paymentMethodData } = paymentData;
    const token = paymentMethodData.tokenizationData.token;
    const { info, description } = paymentMethodData;

    const billingAddress = this.parseBillingAddress(info.billingAddress ?? undefined);
    const formattedAddress = this.formatBillingAddress(billingAddress);
    const tokenData = this.parseToken(token, processor);

    return {
      ...tokenData,
      country: this.region,
      formatted: formattedAddress,
      paymentData: token,
      primary: true,
      type: 'work',
      billingAddress,
      PaymentMethodData: {
        paymentType: info.cardNetwork.toLowerCase(),
        displayName: description,
      },
    };
  }

  private parseBillingAddress(
    billingInfo: google.payments.api.Address | undefined
  ): BillingAddress {
    if (!billingInfo) {
      return {
        streetAddress: '',
        locality: '',
        region: '',
        postalCode: '',
      };
    }
    const streetAddress = [billingInfo.address1, billingInfo.address2].filter(Boolean).join(', ');
    return {
      locality: billingInfo.locality || '',
      postalCode: billingInfo.postalCode || '',
      region: billingInfo.administrativeArea || '',
      streetAddress,
    };
  }

  private formatBillingAddress(address: BillingAddress): string {
    return [address.streetAddress, address.locality, address.region, address.postalCode]
      .filter(Boolean)
      .join(', ');
  }

  private parseToken(
    token: string,
    processor: PaymentProcessor
  ): { signature: string; data: string; version: string } {
    if (processor !== PaymentProcessor.ORBITAL) {
      return { signature: '', data: '', version: '' };
    }

    try {
      const parsedToken = JSON.parse(token);
      return {
        signature: parsedToken.signature || '',
        data: parsedToken.signedMessage || '',
        version: parsedToken.protocolVersion || 'ECv2',
      };
    } catch (error) {
      logger.error({
        message: '[GooglePay] Failed to parse Orbital token',
        error,
      });
      return { signature: '', data: '', version: '' };
    }
  }

  private logPaymentRequest(processor: PaymentProcessor, config: PaymentConfig): void {
    logger.info({
      message: '[GooglePay] Requesting payment',
      platform: 'web',
      processor,
      gateway: config.gateway,
      gatewayMerchantId: config.gatewayMerchantId,
      totalAmount: config.totalAmount,
      currencyCode: config.currencyCode,
      region: config.region,
      environment: config.environment,
    });
  }

  private logPaymentResponse(paymentData: google.payments.api.PaymentData): void {
    logger.info({
      message: '[GooglePay] Payment response received',
      hasPaymentMethodData: !!paymentData?.paymentMethodData,
      responseKeys: Object.keys(paymentData || {}),
      paymentMethodDataKeys: paymentData?.paymentMethodData
        ? Object.keys(paymentData.paymentMethodData)
        : [],
    });
  }
}
